<template>
    <div class="gap-md relative col-span-12 flex w-full flex-col">
        <div class="gap-y-md flex w-full flex-col justify-between">
            <!-- Below md the title row and the create button stack; from md up
                 they share one line as before. -->
            <div class="gap-sm flex flex-col md:flex-row md:items-center md:justify-between">
                <div class="gap-sm flex items-center">
                    <h1>Organizations</h1>
                    <TagComponent
                        :value="numOrgs.toString()"
                        data-testid="organization-count"
                        class-name="text-md aspect-square justify-center font-bold text-(--state-info-fg)"
                    />
                </div>
                <!-- whitespace-normal: ui/Button's base nowrap makes this label
                     overflow the mobile viewport; legacy btn-primary wrapped. -->
                <Button
                    v-if="showCreateButton"
                    class="justify-self-end whitespace-normal"
                    @click="routeToCreationProcess()"
                >
                    Create new organization
                    <i class="i-plus" />
                </Button>
            </div>
            <div class="gap-sm flex">
                <!-- Toggle treatment from notifications-view (ITDEVELOP-2730): active tab
                     on the negative (charcoal) role, inactive on the soft surface. -->
                <Button
                    variant="ghost"
                    :class="showInProcess ? 'bg-soft text-primary' : 'bg-negative text-negative hover:bg-negative'"
                    @click="router.replace({ query: { view: 'established' } })"
                >
                    Established
                </Button>
                <Button
                    variant="ghost"
                    :class="!showInProcess ? 'bg-soft text-primary' : 'bg-negative text-negative hover:bg-negative'"
                    @click="router.replace({ query: { view: 'process' } })"
                >
                    Creation Process
                </Button>
            </div>
        </div>
        <TransitionGroup name="org-list" tag="div" class="gap-md flex flex-col" mode="out-in">
            <!-- <template v-if="displayMode === DisplayMode.LIST"> -->
            <TableList
                v-if="!showInProcess"
                :is-loading="isLoading"
                :sorted-orgs="sortedOrgs"
            />

            <!-- With the wizard flag on and SLA access granted, the Creation Process tab shows
                 the onboarding requests table (ITDEVELOP-3296) instead of the legacy in-process
                 forms. Without a grant the API would answer 403, so the legacy table stays. -->
            <OnboardingRequestsTable
                v-else-if="showOnboardingRequests"
                @open="resumeOnboardingDraft"
                @loaded="onOnboardingRequestsLoaded"
            />

            <ProgressTable v-else :is-loading="isLoading" :sorted-orgs="inProgressItems" @choose="editOrganization" />
            <!-- </template> -->
        </TransitionGroup>
    </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { computed, onMounted, ref, watch } from 'vue';
import { useAdminStore } from '@/stores/admin';
import { mapNextStepToAdminRole } from '@/stores/helpers';
import type { AdminOrganizationData, SortableAdminOrganizationFormData } from '@/stores/store-interfaces';
import TableList from '@/components/admin/organization/table-list.vue';
import router from '@/router/admin-router';
import ProgressTable from '@/components/admin/organization/progress-table.vue';
import OnboardingRequestsTable from '@/components/admin/organization/onboarding-requests-table.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import { resolveCreateOrganizationRoute } from '@/views/admin/organization/org-wizard/org-wizard-routing';
import { useOrgWizardAccess } from '@/views/admin/organization/org-wizard/use-org-wizard-access';

const adminStore = useAdminStore();
const wizardAccess = useOrgWizardAccess();

const queryView = computed(() => router.currentRoute.value.query['view'] as string);
const showInProcess = computed(() => queryView.value === 'process');
const showOnboardingRequests = computed(() => showInProcess.value && wizardAccess.wizardAvailable.value);

// Legacy role check as before; with the wizard on, the SLA WriteCreate grant gates it too
// (docs/sla-access.md), since the button then opens the wizard.
const hasLegacyCreateRole = computed(
    () => adminStore.userCPRoles.includes('admin') || adminStore.userCPRoles.includes('cdim-admin'),
);
const showCreateButton = computed(
    () => hasLegacyCreateRole.value && (!wizardAccess.wizardEnabled || wizardAccess.canCreateInWizard.value),
);
const onboardingRequestCount = ref(0);

function resumeOnboardingDraft(draftId: string) {
    router.push({ name: 'org-wizard-edit', params: { draftId } });
}

function onOnboardingRequestsLoaded(count: number) {
    onboardingRequestCount.value = count;
}

const items = ref<AdminOrganizationData[]>([]);
const inProgressItems = ref<SortableAdminOrganizationFormData[]>([]);
onMounted(async () => {
    if (queryView.value === undefined) {
        router.replace({ query: { view: 'established' } });
    }

    items.value = await adminStore.getOrganizationsByStatus(1);
    inProgressItems.value = await adminStore.getAllInProcessForms().then(
        // use this map to be able to sort by nested values
        (forms) =>
            forms.map((form) => ({
                ...form,
                sort_name: form.organization.name,
                sort_admin: mapNextStepToAdminRole(form.next_step),
            })),
    );
});

const numOrgs = computed(() =>
    showOnboardingRequests.value
        ? onboardingRequestCount.value
        : showInProcess.value
          ? inProgressItems.value.length
          : items.value.length,
);
const isLoading = computed(() => sortedOrgs.value === undefined);

const sortedOrgs = computed(() => {
    return items?.value ?? adminStore.adminsOrganizations; // TODO get rid of this computed
});

function editOrganization(formId: number) {
    router.push(`organization/create/${formId}`);
}

function routeToCreationProcess() {
    router.push(resolveCreateOrganizationRoute());
}

watch(adminStore.adminsOrganizations, async (newValue) => {
    if (newValue !== undefined) {
        console.log(newValue);
        items.value = await adminStore.getOrganizationsByStatus(showInProcess.value ? undefined : 1);
    }
});

watch(showInProcess, async (newValue) => {
    if (newValue !== undefined) {
        items.value = await adminStore.getOrganizationsByStatus(newValue ? undefined : 1);
    }
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.org-list-leave-active {
    transition: opacity 0.15s;
    opacity: 1;
}

.org-list-enter-active {
    transition: opacity 0.15s;
    /* delay the enter transition untill the former has left the DOM completely */
    transition-delay: 0.15s;
    opacity: 1;
}

.org-list-enter-from,
.org-list-leave-to {
    opacity: 0;
}
</style>
